import { useCallback, useEffect, useRef } from 'react';

interface UseInfiniteScrollSentinelOptions {
	hasNextPage: boolean;
	isFetchingNextPage: boolean;
	fetchNextPage: () => void;
	threshold?: number;
}

export function useInfiniteScrollSentinel<T extends HTMLElement = HTMLDivElement>({
	hasNextPage,
	isFetchingNextPage,
	fetchNextPage,
	threshold = 0.1,
}: UseInfiniteScrollSentinelOptions) {
	const sentinelRef = useRef<T>(null);

	const handleIntersect = useCallback(
		(entries: IntersectionObserverEntry[]) => {
			if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
				fetchNextPage();
			}
		},
		[hasNextPage, isFetchingNextPage, fetchNextPage],
	);

	useEffect(() => {
		const el = sentinelRef.current;
		if (!el) return;
		const observer = new IntersectionObserver(handleIntersect, { threshold });
		observer.observe(el);
		return () => observer.disconnect();
	}, [handleIntersect, threshold]);

	return sentinelRef;
}
